import React, { memo, useEffect, useMemo, useState } from "react";

import { Button, message, Modal, Typography } from "antd";
import PropTypes from "prop-types";

import { showAppError } from "../../../Utils/error";
import { KYCVerificationStatus } from "../../../Utils/types";
import UserAPI from "../../api/UserAPI";
import { REQUIRED_DOCUMENTS } from "../userKYCTypes";
import ReviewKYCForm from "./ReviewKYCForm";

// const initialRejectionReasons = {
//   pan: "",
//   authorisedSignatoryPan: "",

//   aadhar: "",
//   authorisedSignatoryAadhar: "",

//   passport: "",
//   authorisedSignatoryPassport: "",

//   residency: "",
//   authorisedSignatoryResidency: "",

//   certificateOfIncorporation: "",

//   gstCertificate: "",
// };

const ReviewKYCModal = ({
  kyc,
  setKyc,
  isModalOpen,
  setIsModalOpen,
  reloadUserKYCList,
}) => {
  const [formMode, setFormMode] = useState(
    kyc?.country === "IN" ? "IN" : "INTL"
  );
  const [authority, setAuthority] = useState(kyc?.authority || "individual");
  const [rejectionReasons, setRejectionReasons] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const requiredDocuments = useMemo(
    () => REQUIRED_DOCUMENTS?.[formMode]?.[authority],
    [formMode, authority]
  );

  const statusToSave = useMemo(
    () =>
      Object.values(rejectionReasons).some((reason) => reason?.trim())
        ? KYCVerificationStatus.REJECTED
        : KYCVerificationStatus.VERIFIED,
    [rejectionReasons]
  );

  const initialRejectionReasons = useMemo(() => {
    const kycRejectionReasons = kyc?.rejectionReasons || {};

    return {
      pan: kycRejectionReasons.pan?.[0] || "",
      authorisedSignatoryPan:
        kycRejectionReasons.authorisedSignatoryPan?.[0] || "",

      aadhar: kycRejectionReasons.aadhar?.[0] || "",
      authorisedSignatoryAadhar:
        kycRejectionReasons.authorisedSignatoryAadhar?.[0] || "",

      passport: kycRejectionReasons.passport?.[0] || "",
      authorisedSignatoryPassport:
        kycRejectionReasons.authorisedSignatoryPassport?.[0] || "",

      residency: kycRejectionReasons.residency?.[0] || "",
      authorisedSignatoryResidency:
        kycRejectionReasons.authorisedSignatoryResidency?.[0] || "",

      certificateOfIncorporation:
        kycRejectionReasons.certificateOfIncorporation?.[0] || "",

      gstCertificate: kycRejectionReasons.gstCertificate?.[0] || "",
    };
  }, [kyc?.rejectionReasons]);

  const onAddRejectionReason = (key, value) => {
    setRejectionReasons((prev) => ({ ...prev, [key]: value }));
  };

  const handleCloseModal = () => {
    setKyc(null);
    setIsModalOpen(false);
  };

  const reset = () => {
    setFormMode("IN");
    setAuthority("individual");
    setIsSaving(false);
    setRejectionReasons(initialRejectionReasons);
  };

  const submitReview = async () => {
    setIsSaving(true);

    try {
      const resp = await UserAPI.verifyUserKYC(kyc?._id, {
        status: statusToSave,
        rejectionReasons,
      });

      if (resp?.code === 200) {
        message.success(
          `KYC has been ${
            statusToSave === KYCVerificationStatus.VERIFIED
              ? "approved"
              : "rejected"
          } successfully`
        );
        reloadUserKYCList(statusToSave);
        handleCloseModal();
      } else {
        showAppError(resp);
      }
    } catch (error) {
      showAppError(error);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (!isModalOpen) {
      reset();
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (isModalOpen) {
      setFormMode(kyc?.country === "IN" ? "IN" : "INTL");
      setAuthority(kyc?.authority || "individual");
    }
  }, [isModalOpen, kyc?.authority, kyc?.country]);

  useEffect(() => {
    setRejectionReasons(initialRejectionReasons);
  }, [kyc?.rejectionReasons]);

  return (
    <Modal
      title={
        <div>
          <Typography.Title level={5} style={{ marginBottom: 0 }}>
            {kyc?.verificationStatus === KYCVerificationStatus.UNDER_REVIEW
              ? "Review KYC Documents"
              : "View KYC Documents"}
          </Typography.Title>
        </div>
      }
      centered
      footer={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "end",
            gap: 4,
          }}
        >
          <>
            <Button className="siteBtn grayBtn" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button
              className={
                statusToSave === KYCVerificationStatus.VERIFIED
                  ? "siteBtn accentBtnV1 successBtn"
                  : "siteBtn accentBtnV1 danger"
              }
              loading={isSaving}
              onClick={submitReview}
              disabled={statusToSave === kyc?.verificationStatus || isSaving}
            >
              {statusToSave === KYCVerificationStatus.VERIFIED
                ? "Approve"
                : "Reject"}
            </Button>
          </>
        </div>
      }
      open={isModalOpen}
      onCancel={handleCloseModal}
      className="createPostModalv2 addSubscriberModal review-kyc-modal"
      wrapClassName="createPostModalDialogv2"
      maskClosable={false}
      width={800}
      destroyOnClose
    >
      <ReviewKYCForm
        kyc={kyc}
        isModalOpen={isModalOpen}
        formMode={formMode}
        authority={authority}
        requiredDocuments={[...requiredDocuments]}
        rejectionReasons={rejectionReasons}
        onAddRejectionReason={onAddRejectionReason}
      />
    </Modal>
  );
};

ReviewKYCModal.propTypes = {
  kyc: PropTypes.shape().isRequired,
  setKyc: PropTypes.func.isRequired,
  isModalOpen: PropTypes.bool.isRequired,
  setIsModalOpen: PropTypes.func.isRequired,
  reloadUserKYCList: PropTypes.func.isRequired,
};

export default memo(ReviewKYCModal);
